import type { Device } from '@twilio/voice-sdk'
import { changerEtatTelephone } from '@/stores/useEtatTelephoneStore'

/**
 * Singleton du Device Twilio Voice SDK.
 *
 * Lib pure (pas de React) — un seul Device par session, partagé.
 * Le lifecycle React est géré par hooks/useTwilioDevice.ts.
 */

let deviceInstance: Device | null = null
let isInitializing = false
let tokenRefreshCallback: (() => Promise<void>) | null = null
/** Set by useTwilioDevice: builds the Device again when there is none. */
let relanceur: (() => void) | null = null

const isDev = import.meta.env.DEV
const devLog = (...args: unknown[]) => {
  if (isDev) console.log(...args)
}
const devWarn = (...args: unknown[]) => {
  if (isDev) console.warn(...args)
}
const devError = (...args: unknown[]) => {
  if (isDev) console.error(...args)
}

export function getDevice(): Device | null {
  return deviceInstance
}

export interface DeviceOptions {
  logLevel?: number
  closeProtection?: boolean
}

/**
 * Init du Device Twilio. Idempotent : retourne l'instance existante si déjà créée.
 * DOIT être appelé après un geste utilisateur (click/tap) pour autoriser le micro.
 */
export async function ensureDevice(
  token: string,
  options: DeviceOptions = {},
): Promise<Device> {
  if (deviceInstance) {
    devLog('♻️ Réutilisation du Device Twilio singleton existant')
    return deviceInstance
  }

  if (isInitializing) {
    devLog('⏳ Initialisation Device singleton en cours, attente…')
    while (isInitializing && !deviceInstance) {
      await new Promise((resolve) => setTimeout(resolve, 100))
    }
    return deviceInstance!
  }

  devLog('🔄 Création nouveau Device Twilio singleton')
  isInitializing = true

  try {
    const { Device: DeviceCtor } = await import('@twilio/voice-sdk')

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    deviceInstance = new DeviceCtor(token, {
      // Twilio Voice SDK log levels : 0=trace, 1=debug, 2=info, 3=warn, 4=error, 5=silent.
      // En prod on monte à 'warn' : on garde les warnings critiques (token expiry,
      // ICE failure, etc.) sans noyer la console client de logs WebRTC verbeux.
      logLevel: (isDev ? 'debug' : 'warn') as any,
      codecPreferences: ['opus', 'pcmu'] as any,
      closeProtection: true,
      ...options,
    } as any)

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    deviceInstance.on('error', (error: any) => {
      if (error.message?.includes('Insights') || error.message?.includes('eventgw')) {
        devWarn('⚠️ Twilio Insights non disponible (non critique):', error.message)
      } else if (error.code === 31401 || error.message?.includes('31401')) {
        devError('❌ Permission microphone refusée (31401)')
      } else {
        devError('❌ Erreur Device Twilio:', error)
      }
    })

    deviceInstance.on('offline', () => {
      devWarn('⚠️ Device Twilio déconnecté (offline)')
    })

    // Refresh proactif du token Twilio (~5min avant expiration)
    deviceInstance.on('tokenWillExpire', async () => {
      devWarn('⚠️ Token Twilio va expirer, rafraîchissement proactif…')
      if (tokenRefreshCallback) {
        try {
          await tokenRefreshCallback()
          devLog('✅ Token Twilio rafraîchi via tokenWillExpire')
        } catch (error) {
          devError('❌ Erreur lors du refresh proactif du token:', error)
        }
      }
    })

    devLog('✅ Device Twilio singleton créé')
    return deviceInstance
  } finally {
    isInitializing = false
  }
}

export function destroyDevice(): void {
  if (!deviceInstance) return
  devLog('🧹 Destruction du Device Twilio singleton')
  try {
    deviceInstance.disconnectAll()
    deviceInstance.removeAllListeners()
    deviceInstance.unregister()
    deviceInstance.destroy()
    devLog('✅ Device Twilio singleton détruit')
  } catch (error) {
    devWarn('⚠️ Erreur lors de la destruction du Device:', error)
  }
  deviceInstance = null
  isInitializing = false
  changerEtatTelephone('twilio', 'eteint')
}

export function setRelanceurTwilio(callback: (() => void) | null): void {
  relanceur = callback
}

/**
 * Brings the phone back now: Reconnect button, a call placed while it is not
 * ready, the health check. useTwilioDevice decides how: a fresh token, a new
 * Device (after a failed start), or registering again.
 */
export function relancerTwilio(): void {
  if (isInitializing || deviceInstance?.state === 'registered') return
  if (deviceInstance?.state === 'registering' || deviceInstance?.state === 'destroyed') return
  relanceur?.()
}

export function isDeviceReady(): boolean {
  return deviceInstance?.state === 'registered'
}

export function getDeviceState(): string | null {
  return deviceInstance?.state || null
}

/**
 * Enregistre un callback pour le rafraîchissement du token Twilio.
 * Sera appelé automatiquement quand le device émet 'tokenWillExpire'.
 */
export function setTokenRefreshCallback(
  callback: (() => Promise<void>) | null,
): void {
  tokenRefreshCallback = callback
}
